// MARK://dhk8/pos/components/ErrorBoundary
// App-level error boundary. POS is a long-running kiosk surface — a thrown render
// error must not leave a white screen. Catches, shows a recoverable panel, and lets
// the operator retry without reloading (which would drop in-memory cart state).

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { t } from '../i18n/es-MX.js'

interface Props {
  children: ReactNode
  /** Optional custom fallback renderer. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[pos] render error:', error, info.componentStack)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)
    return (
      <div className="dhk8-pos-errbound" role="alert">
        <div className="dhk8-pos-errbound__card">
          <h1>{t('error.boundaryTitle')}</h1>
          <p>{t('error.boundaryBody')}</p>
          {import.meta.env.DEV && <pre className="dhk8-pos-errbound__detail">{error.message}</pre>}
          <button type="button" onClick={this.reset}>{t('common.retry')}</button>
        </div>
        <style>{styles}</style>
      </div>
    )
  }
}

// Tokens --pos-* (tokens.css) — antes leía una capa base genérica (--bg/--accent azul)
// que dejaba la pantalla de crash fuera de marca. Fallbacks = valores del tema oscuro
// (default) por si el token faltara durante el fallo.
const styles = `
  .dhk8-pos-errbound {
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: var(--pos-canvas, #0F1411);
    color: var(--pos-fg, #E8EFE9);
    font-family: inherit;
  }
  .dhk8-pos-errbound__card {
    max-width: 420px;
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-radius: var(--pos-radius-lg, 12px);
    background: var(--pos-surface, #161C18);
    box-shadow: var(--pos-shadow-2, 0 4px 12px rgba(0,0,0,.24));
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .dhk8-pos-errbound__card h1 { margin: 0; font-size: 1.25rem; }
  .dhk8-pos-errbound__card p { margin: 0; color: var(--pos-fg-secondary, #B6BFB8); font-size: 0.9rem; line-height: 1.4; }
  .dhk8-pos-errbound__detail {
    margin: 0; padding: 8px; font-size: 0.75rem; overflow-x: auto;
    border-radius: var(--pos-radius-sm, 6px);
    background: var(--pos-danger-bg, rgba(229,72,77,.16));
    border: 1px solid var(--pos-separator, rgba(255,255,255,.07));
    color: var(--pos-danger, #E5484D);
  }
  .dhk8-pos-errbound__card button {
    align-self: flex-start; padding: 8px 16px; cursor: pointer;
    border-radius: var(--pos-radius-md, 8px);
    background: var(--pos-tint, #1F8B3A); color: var(--pos-on-tint, #fff);
    border: none; font: inherit; font-weight: 600;
  }
`
